import { Alert, PageHeader, Spinner } from '@cartly/ui';
import { Link } from 'react-router';
import { api } from '../../api/client';
import { useLoader } from '../../app/hooks';
import { useActiveSession, useSession } from '../../app/session';
import { CampaignSections } from '../whatsapp/Campaigns';
import { countBodyParams } from '../whatsapp/Messaging';

interface AccountRow {
  phoneNumbers: {
    id: string;
    displayPhoneNumber: string;
    verifiedName: string | null;
    registeredAt: string | null;
    isOnBizApp: boolean;
  }[];
}
interface TemplateRow {
  name: string;
  language: string;
  status: string;
  components: unknown;
}

/** Scheduled WhatsApp template campaigns to customer segments. */
export function CampaignsPage() {
  const session = useActiveSession();
  const { can } = useSession();
  const tenantId = session.activeTenant?.id;
  const accounts = useLoader(
    () => api.get<{ accounts: AccountRow[] }>('/whatsapp/accounts').then((r) => r.accounts),
    tenantId,
  );
  const templates = useLoader(
    () => api.get<{ templates: TemplateRow[] }>('/whatsapp/templates').then((r) => r.templates),
    tenantId,
  );

  if ((accounts.loading && !accounts.data) || (templates.loading && !templates.data)) {
    return <Spinner size="lg" label="Loading" />;
  }
  if (accounts.error || !accounts.data) {
    return <Alert tone="critical">{accounts.error ?? 'Failed to load'}</Alert>;
  }
  const phones = accounts.data.flatMap((a) =>
    a.phoneNumbers.map((p) => ({
      id: p.id,
      label: `${p.displayPhoneNumber}${p.verifiedName ? ` · ${p.verifiedName}` : ''}`,
      registeredAt: p.registeredAt,
      coexistence: p.isOnBizApp,
    })),
  );

  return (
    <div className="stack-lg">
      <PageHeader
        title="Campaigns"
        description="Scheduled template campaigns to customer segments. Only customers with a recorded WhatsApp opt-in are messaged."
      />
      {phones.length === 0 ? (
        <Alert tone="info" title="Connect WhatsApp first">
          Campaigns send from a WhatsApp number. <Link to="/whatsapp">Connect an account</Link>,
          sync templates, and create a segment under{' '}
          <Link to="/customers/segments">Customers → Segments</Link>.
        </Alert>
      ) : (
        <CampaignSections
          show="campaigns"
          tenantId={tenantId}
          phones={phones}
          templates={(templates.data ?? []).map((t) => ({
            name: t.name,
            language: t.language,
            status: t.status,
            bodyParams: countBodyParams(t.components),
          }))}
          canManageChannels={can('channels:manage')}
          canReadCampaigns={can('campaigns:read')}
          canManageCampaigns={can('campaigns:manage')}
        />
      )}
    </div>
  );
}
